import { useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import type { Finding, ModuleRun, ReportSummary } from '@/types/forensics';

interface ResultsData {
  summary: ReportSummary | null;
  findings: Finding[];
  moduleRuns: ModuleRun[];
  metadataSummary: Record<string, unknown>;
}

export function useResults(jobId: string | undefined) {
  const [data, setData] = useState<ResultsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchResults = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/jobs/${jobId}/results`);
      if (!response.ok) throw new Error('Failed to fetch results');
      const json = await response.json();
      setData({
        summary: json.summary,
        findings: json.findings || [],
        moduleRuns: json.moduleRuns || [],
        metadataSummary: json.metadataSummary || {},
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, loading, error, fetchResults, reset };
}

export function useExportReport(jobId: string | undefined) {
  const [loading, setLoading] = useState(false);

  const exportReport = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const response = await apiFetch(`/api/jobs/${jobId}/export/report.json`);
      if (!response.ok) throw new Error('Failed to export report');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forensics_report_${jobId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  return { exportReport, loading };
}
